import React, { useRef, useMemo, useState, useCallback } from "react";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import TextBox from "devextreme-react/text-box";
import { FileUploader } from "devextreme-react";
import { useNavigate } from "react-router-dom";
import useFetch from "../../Hooks/useFetch";
import { isMobile } from "../../Utils/media-query";
import { apiBaseURL } from "../../Utils/ProjectConstants";

export default function ActionNeededPurchaseOrders({
  orderId,
  setNotifications,
  onClose,
  mutate,
}) {
  const navigate = useNavigate();
  const fetch = useFetch(navigate);

  const actionNeededReason = useRef("");
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------------- COMMENT ----------------
  const onValueChanged = (e) => {
    actionNeededReason.current = e.value;
  };

  // ---------------- FILE SELECTION ----------------
  const handleFileChanged = useCallback((e) => {
    setAttachments(e.value || []);
  }, []);

  // ---------------- SUBMIT ----------------
  const submitActionNeeded = useCallback(async () => {
    const comment = actionNeededReason.current?.trim();

    if (!comment) {
      setNotifications?.({
        message: "Please enter a comment.",
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();

      formData.append("orderId", orderId);
      formData.append("comment", comment);

      // If multiple files are selected, append each file
      attachments.forEach((file) => {
        formData.append("file", file);
      });

      const response = await fetch.postFile(
        `${apiBaseURL}/api/Approval/PurchaseOrder/RequestAction`,
        formData,
      );

      console.log("Request Action Response:", response);

      await mutate?.();

      setNotifications(response);

      onClose?.();
    } catch (error) {
      console.error("Request Action Error:", error);

      setNotifications?.({
        message: "Unable to submit action request.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [orderId, attachments, fetch, mutate, onClose, setNotifications]);

  // ---------------- BUTTON OPTIONS ----------------
  const submitOptions = useMemo(
    () => ({
      stylingMode: "contained",
      type: "danger",
      text: isSubmitting ? "Submitting..." : "Request Action",
      disabled: isSubmitting,
      onClick: submitActionNeeded,
    }),
    [submitActionNeeded, isSubmitting],
  );

  const cancelOptions = useMemo(
    () => ({
      stylingMode: "outlined",
      text: "Cancel",
      disabled: isSubmitting,
      onClick: onClose,
    }),
    [onClose, isSubmitting],
  );

  return (
    <Popup
      visible={true}
      onHiding={onClose}
      hideOnOutsideClick={!isSubmitting}
      title={`Request Action - ${orderId}`}
      width={isMobile ? "100%" : 500}
      height="auto"
      resizeEnabled={true}
    >
      <div style={{ padding: "12px" }}>
        <div
          style={{
            marginBottom: "8px",
            fontWeight: 500,
            color: "white",
          }}
        >
          Please enter a comment explaining why action is needed.
        </div>

        <TextBox
          spellcheck={true}
          placeholder="Type comment..."
          onValueChanged={onValueChanged}
          valueChangeEvent="input"
          height={50}
        />

        <div style={{ marginTop: "15px" }}>
          <FileUploader
            selectButtonText="Upload Attachment"
            labelText=""
            uploadMode="useForm"
            multiple={true}
            showFileList={true}
            value={attachments}
            onValueChanged={handleFileChanged}
          />
        </div>
      </div>

      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={cancelOptions}
      />

      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={submitOptions}
      />
    </Popup>
  );
}
